import type { BookDrivingLessonParams, CreateDrivingLessonFormValues } from "./CreateDrivingLessonForm";
import type {
  BookSimulatorDrivingLessonParams,
  CreateSimulatorDrivingLessonFormValues,
} from "./CreateSimulatorDrivingLessonForm";
import type { BookPracticalExamParams, BookTheoryExamParams, CreateExamFormValues } from "./CreateExamForm";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import Typography from "@mui/material/Typography";
import { getPriceSuggestion, GetPriceSuggestionRequestSchema } from "../../api/backoffice.api";
import { Money } from "../../model/Money";
import { moneyFormatter } from "../../misc/Money";
import { Row } from "../../misc/Row";
import { LinearProgress } from "react-admin";

type Params =
  | BookTheoryExamParams
  | BookPracticalExamParams
  | BookDrivingLessonParams
  | Omit<BookSimulatorDrivingLessonParams, "simulatorId">;

interface PriceSuggestionProps {
  validate: (formValues: any) => Params;
}

export const PriceSuggestion = ({ validate }: PriceSuggestionProps) => {
  const [price, setPrice] = useState<undefined | "loading" | Money>();
  const [authorityFee, setAuthorityFee] = useState<undefined | Money>();
  const { getValues, watch } = useFormContext<
    CreateExamFormValues | CreateDrivingLessonFormValues | CreateSimulatorDrivingLessonFormValues
  >();
  const priceSuggestionCache = useRef<Record<string, ReturnType<typeof getPriceSuggestion>>>({}).current;
  const formValues = watch();
  const drivingLessonType = "examType" in formValues ? formValues.examType : formValues.drivingLessonType;
  useEffect(() => {
    setPrice(undefined);
    setAuthorityFee(undefined);
    let params: Params;
    try {
      params = validate(formValues);
    } catch (error) {
      console.info(`Will not get price suggestion yet -- form is invalid: ${error}`);
      return;
    }
    void (async () => {
      try {
        const duration = "duration" in params ? params.duration : undefined;
        const request = GetPriceSuggestionRequestSchema.parse({
          eventType: "DrivingLesson",
          drivingLessonType: ["unterweisungAmFahrzeug", "praktischeUnterweisung"].includes(drivingLessonType)
            ? "normal"
            : drivingLessonType,
          ...(params.instructorId ? { instructorUid: params.instructorId } : {}),
          studentUid: params.studentId,
          bookedTrainingId: params.bookedTrainingId,
          start: params.dateTime,
          ...(duration ? { end: params.dateTime.plus(duration) } : {}),
        });
        const cacheKey = JSON.stringify(request);
        let promise = priceSuggestionCache[cacheKey];
        if (!promise) {
          setPrice("loading");
          priceSuggestionCache[cacheKey] = promise = getPriceSuggestion(request);
        }
        const priceSuggestion = await promise;
        let priceSuggestionMatchesFormValues = false;
        try {
          const params2 = validate(getValues());
          priceSuggestionMatchesFormValues =
            ("examType" in params && "examType" in params2 ? params.examType === params2.examType : true) &&
            ("duration" in params && "duration" in params2
              ? params.duration?.minutes === params2.duration?.minutes
              : true) &&
            params.instructorId === params2.instructorId &&
            params.studentId === params2.studentId &&
            params.bookedTrainingId === params2.bookedTrainingId &&
            params.dateTime === params2.dateTime;
        } catch {
          // Ignored: form is invalid
        }
        if (priceSuggestionMatchesFormValues) {
          setPrice(priceSuggestion.price);
          setAuthorityFee(priceSuggestion.authorityFee);
        }
      } catch (error) {
        console.error("Failed to get price suggestion", error);
      }
    })();
  }, [
    drivingLessonType,
    formValues.instructorId,
    formValues.student?.id,
    formValues.drivingLicenseClass,
    formValues.dateTime,
    formValues.durationInMinutes,
    validate,
  ]);
  const showPrice = !!price;
  const showAuthorityFee =
    authorityFee && (drivingLessonType === "theoretischePruefung" || drivingLessonType === "praktischePruefung");

  return (
    <Row sx={{ alignItems: "center", "& .MuiTypography-root": { fontSize: "14px" } }}>
      {/* [UX] Render a non-breaking space if no price is shown to prevent the dialog from jumping ... */}
      {!(showPrice || showAuthorityFee) && <Typography>{"\u00A0"}</Typography>}
      {showPrice && <Typography>{"Preis:\u00A0 "}</Typography>}
      {price === "loading" ? (
        <div>
          <LinearProgress sx={{ width: "4em" }} timeout={0} />
        </div>
      ) : (
        price && <Typography>{moneyFormatter.format(price.amount)}</Typography>
      )}
      {showPrice && showAuthorityFee && <Typography>{"\u00A0+\u00A0"}</Typography>}
      {showAuthorityFee && <Typography>TÜV-/DEKRA-Gebühr: {moneyFormatter.format(authorityFee.amount)}</Typography>}
    </Row>
  );
};
